import { useProcurement } from "@/context/ProcurementContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/lib/utils";
import { 
  ShoppingCart, 
  Eye, 
  Calendar,
  Package,
  User,
  ArrowRight,
  Trash2
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

interface RecentOrdersProps {
  onNavigate?: (path: string) => void;
}

export const RecentOrders = ({ onNavigate }: RecentOrdersProps) => {
  const { state, actions } = useProcurement();
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Get recent purchase orders (last 5)
  const recentOrders = (state.purchaseOrders ?? [])
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5);

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'default';
      case 'Created/New':
        return 'secondary';
      case 'Approved':
        return 'outline';
      case 'Sent to Vendor':
        return 'outline';
      case 'Partially Received':
        return 'secondary';
      case 'Received':
        return 'default';
      case 'Cancelled':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getVendorName = (vendorId: number) => {
    const vendor = (state.vendors ?? []).find(v => v.id === vendorId);
    return vendor?.name || `Vendor ${vendorId}`;
  };

  const getUserName = (userId: number) => {
    const user = (state.users ?? []).find(u => u.id === userId);
    return user?.name || `User ${userId}`;
  };

  const handleDeletePO = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this purchase order?')) {
      setDeletingId(id);
      try {
        const success = await actions.deletePurchaseOrder(id);
        if (success) {
          toast.success('Purchase order deleted successfully');
        } else {
          toast.error('Failed to delete purchase order');
        }
      } catch (error) {
        toast.error('Failed to delete purchase order');
      } finally {
        setDeletingId(null);
      }
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              Recent Purchase Orders
            </CardTitle>
            <CardDescription>
              Latest purchase order activity
            </CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => onNavigate?.('/purchase-orders')}
          >
            View All
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {recentOrders.length > 0 ? (
          <div className="space-y-4">
            {recentOrders.map((order) => (
              <div key={order.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold">{order.poNumber}</h4>
                    {order.status === 'APPROVED' ? (
                      <span className="badge bg-success text-white px-2 py-1 rounded text-xs">
                        {order.status}
                      </span>
                    ) : (
                      <Badge variant={getStatusVariant(order.status)} className="text-xs">
                        {order.status}
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {getVendorName(order.vendorId)}
                    </div>
                    <div className="flex items-center gap-1">
                      <Package className="h-3 w-3" />
                      {order.items.length} item{order.items.length !== 1 ? 's' : ''}
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {new Date(order.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      Created by: {getUserName(order.createdBy)}
                    </span>
                    <span className="font-semibold">
                      {formatCurrency(order.totalAmount)}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => onNavigate?.(`/purchase-orders?view=${order.id}`)}
                    title="View Details"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleDeletePO(order.id)}
                    disabled={deletingId === order.id}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    title="Delete PO"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No purchase orders found</p>
            <p className="text-sm">Recent orders will appear here</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};